import { ApolloLink, Observable } from 'apollo-link';
import { gql } from 'graphql-tag';
import type { InMemoryCache } from 'apollo-cache-inmemory';
import { print } from 'graphql';
import isEqual from 'lodash/isEqual';

import { getClientStream } from '@confluence/ssr-data-streaming';

type ResultError = {
	message: string;
	path: string[];
	locations: any;
};

type Result = {
	result: any;
	errors: ResultError[];
};

function hasRootLevelErrors(eventResult: Result) {
	if (!Array.isArray(eventResult.errors)) {
		return false;
	}

	return eventResult.errors.some((error: ResultError) => error.path && error.path.length === 1);
}

class QueryResultCache {
	private queryResults: Map<string, { result: object; variables: object }[]>;
	constructor(private cache: InMemoryCache) {
		this.queryResults = new Map();

		const data = getClientStream('cgraphql');

		data.addListener((event) => {
			if (event.type === 'result') {
				try {
					if (!hasRootLevelErrors(event.result)) {
						this.cache.writeQuery({
							query: gql(event.operation),
							variables: event.variables,
							data: event.result.data,
						});
						this.addResult(event.operation, event.result.data, event.variables);
					}
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('Failed to write query', e);
				}
			}
		});
	}

	getMatchingResult(query: string, variables: object) {
		const results = this.queryResults.get(query);

		if (!results) {
			return null;
		}

		const matchingQueryResult = results.findIndex((result) => isEqual(result.variables, variables));

		if (matchingQueryResult === -1) {
			return null;
		}

		const matchingData = results[matchingQueryResult].result;
		results.splice(matchingQueryResult, 1);
		this.queryResults.set(query, results);
		return matchingData;
	}

	addResult(query: string, result: object, variables: object) {
		const results = this.queryResults.get(query) || [];
		results.push({ result, variables });
		this.queryResults.set(query, results);
	}
}

export const SSRClientLink = (cache: InMemoryCache): ApolloLink => {
	let queryResultCache: QueryResultCache | null = null;

	if (window.__SSR_REACT_STREAMING__) {
		queryResultCache = new QueryResultCache(cache);
	}

	return new ApolloLink((operation, forward) => {
		if (!window.__SSR_REACT_STREAMING__ || !queryResultCache) {
			return forward(operation);
		}

		const query = print(operation.query);
		const result = queryResultCache.getMatchingResult(query, operation.variables);
		if (result) {
			return Observable.of({
				data: result,
			});
		}

		return forward(operation);
	});
};
